import PropTypes from 'prop-types';
import { Navigate, useLocation } from 'react-router-dom';

// project imports
import useAuth from 'hooks/useAuth';
import { DASHBOARD_PATH } from 'config';

// ==============================|| AUTH GUARD ||============================== //

/**
 * Authentication guard for routes
 * @param {PropTypes.node} children children element/node
 */
const AuthGuard = ({ children, allowedPrincipalTypes = [] }) => {
    const { isLoggedIn, user } = useAuth();
    const location = useLocation();
    const candidateAssessmentIdFromPath = location.pathname.startsWith('/candidate/') ? location.pathname.split('/')[2] || '' : '';
    const guestRequestCodeFromPath = location.pathname.startsWith('/guest/') ? location.pathname.split('/')[2] || '' : '';
    const userAssessmentCode = user?.assessmentCode || user?.assessmentId || '';
    const guestRequestCode = user?.requestCode || user?.assessmentId || '';

    if (!isLoggedIn) {
        const loginPath = location.pathname.startsWith('/candidate/')
            ? `/candidate/${candidateAssessmentIdFromPath}/login`
            : location.pathname.startsWith('/guest/') && guestRequestCodeFromPath
            ? `/guest/${guestRequestCodeFromPath}/login`
            : '/login';
        return <Navigate to={loginPath} replace state={{ from: location }} />;
    }

    const hasPrincipalRestriction = allowedPrincipalTypes.length > 0;
    const currentPrincipalType = user?.principalType || 'user';

    if (hasPrincipalRestriction && !allowedPrincipalTypes.includes(currentPrincipalType)) {
        const fallbackPath =
            currentPrincipalType === 'candidate'
                ? userAssessmentCode
                    ? `/candidate/${userAssessmentCode}`
                    : '/login'
                : currentPrincipalType === 'guest'
                ? guestRequestCode
                    ? `/guest/${guestRequestCode}`
                    : '/login'
                : DASHBOARD_PATH;
        return <Navigate to={fallbackPath} replace />;
    }

    if (
        currentPrincipalType === 'candidate' &&
        location.pathname.startsWith('/candidate/') &&
        userAssessmentCode &&
        candidateAssessmentIdFromPath &&
        candidateAssessmentIdFromPath !== userAssessmentCode &&
        candidateAssessmentIdFromPath !== user?.assessmentId
    ) {
        return <Navigate to={`/candidate/${userAssessmentCode}/`} replace />;
    }

    if (
        currentPrincipalType === 'guest' &&
        location.pathname.startsWith('/guest/') &&
        guestRequestCode &&
        guestRequestCodeFromPath &&
        guestRequestCodeFromPath !== guestRequestCode
    ) {
        return <Navigate to={`/guest/${guestRequestCode}`} replace />;
    }

    return children;
};

AuthGuard.propTypes = {
    children: PropTypes.node,
    allowedPrincipalTypes: PropTypes.arrayOf(PropTypes.string)
};

export default AuthGuard;
